import { useApolloClient, useSubscription } from '@apollo/client';
import { useCallback, useState } from 'react';
import { GenerateTrainersNotesDocument, TrainersNotesGenerationUpdatesDocument } from '../Trainee/TraineeReports.api';
import { TrainersNotesGenerationState } from '@/apolloClient.types';
import { useUserSession } from '@/contexts/UserSession';

interface UseTrainersNotesGenerationConfig {
    assessmentRunId: string;
    userId: string; // trainee id
    companyId: string;
    onSuccess?: (suggestion: string) => void;
    onError?: (errorMessage: string) => void;
}

interface UseTrainersNotesGenerationResult {
    isGenerating: boolean;
    generate: () => Promise<void>;
}

/**
 * Hook to manage trainers notes AI generation via subscription.
 * Uses subscription for real-time updates instead of blocking mutation.
 * No document is created - suggestion is delivered directly via subscription.
 * Document is only created when user explicitly saves (Save Draft/Publish).
 */
const useTrainersNotesGeneration = (config: UseTrainersNotesGenerationConfig): UseTrainersNotesGenerationResult => {
    const { assessmentRunId, userId, companyId, onSuccess, onError } = config;
    const apolloClient = useApolloClient();
    const { token } = useUserSession();

    const [isGenerating, setIsGenerating] = useState(false);

    // Subscribe to generation updates when generating
    // Channel is scoped to trainer + run + trainee to prevent cross-trainee updates
    useSubscription(TrainersNotesGenerationUpdatesDocument, {
        variables: {
            token: token!,
            assessmentRunId,
            traineeUserId: userId,
        },
        skip: !token || !companyId,
        shouldResubscribe: true,
        ignoreResults: true,
        onData: ({ data: subscriptionData }) => {
            const payload = subscriptionData.data?.eventFromTrainersNotesGeneration;
            if (!payload) {
                return;
            }

            const { state, suggestion, error } = payload;

            if (state === TrainersNotesGenerationState.Idle) {
                setIsGenerating(false);
                const normalizedSuggestion = (suggestion ?? '').trim();
                if (normalizedSuggestion) {
                    onSuccess?.(normalizedSuggestion);
                } else {
                    onError?.('Generation completed but returned no content');
                }
            } else if (state === TrainersNotesGenerationState.Failed) {
                setIsGenerating(false);
                onError?.(error || 'Generation failed');
            }
            // If still Generating, keep isGenerating true
        },
    });

    const generate = useCallback(async () => {
        if (!companyId || !token) {
            return;
        }

        setIsGenerating(true);

        try {
            const { data } = await apolloClient.mutate({
                mutation: GenerateTrainersNotesDocument,
                variables: {
                    input: {
                        assessmentRunId,
                        userId,
                        companyId,
                    },
                },
            });

            if (data?.generateTrainersNotes.__typename === 'ErrorResponse') {
                const msg = data.generateTrainersNotes.fields?.[0]?.message || 'Unknown error';
                setIsGenerating(false);
                onError?.(msg);
            }
            // If queued successfully, subscription will handle the rest
        } catch (error: any) {
            setIsGenerating(false);
            onError?.(error?.message || 'Unknown error');
        }
    }, [apolloClient, assessmentRunId, companyId, onError, token, userId]);

    return {
        isGenerating,
        generate,
    };
};

export default useTrainersNotesGeneration;
